import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/auth";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Tag } from "lucide-react";
import { useConfirm } from "@/lib/confirm";

const EMPTY = { code: "", description: "", discount_type: "fixed", value: 5, applies_to: "all", batch_id: "", max_uses: 0, starts_at: "", ends_at: "", active: true, show_banner: false, banner_text: "" };

export default function PromoCodes() {
  const [codes, setCodes] = useState([]);
  const [report, setReport] = useState({});
  const [batches, setBatches] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [editing, setEditing] = useState(null);
  const confirm = useConfirm();

  const load = useCallback(async () => {
    const [c, r, b, s] = await Promise.all([api.get("/admin/promo-codes"), api.get("/admin/promo-codes/report"), api.get("/batches"), api.get("/settings")]);
    setCodes(c.data); setReport(r.data); setBatches(b.data); setEnabled(!!s.data?.promo_codes_enabled);
  }, []);
  useEffect(() => { load().catch(() => toast.error("Failed to load promo codes")); }, [load]);

  const toggleEnabled = async () => {
    await api.put("/admin/settings", { promo_codes_enabled: !enabled });
    setEnabled(!enabled); toast.success(!enabled ? "Promo codes are now accepted at checkout" : "Promo codes disabled at checkout");
  };
  const save = async (form) => {
    const body = { ...form, code: form.code.trim().toUpperCase(), value: Number(form.value), max_uses: Number(form.max_uses) || 0,
      batch_name: batches.find(b => b.id === form.batch_id)?.name || "" };
    if (!body.code || !(body.value > 0)) return toast.error("Code and a value above 0 are required");
    if (body.discount_type === "percent" && body.value > 100) return toast.error("Percent can't exceed 100");
    if (form.id) await api.put(`/admin/promo-codes/${form.id}`, body); else await api.post("/admin/promo-codes", { ...body, uses: 0 });
    toast.success("Saved"); setEditing(null); load();
  };
  const remove = async (c) => {
    if (!(await confirm({ title: `Delete ${c.code}?`, description: "Customers will no longer be able to use it.", danger: true, confirmLabel: "Delete" }))) return;
    await api.delete(`/admin/promo-codes/${c.id}`); toast.success("Deleted"); load();
  };
  const fmt = (c) => c.discount_type === "percent" ? `${c.value}% off` : `$${Number(c.value).toFixed(2)} off`;
  const scope = (c) => c.batch_id ? `Batch: ${c.batch_name || c.batch_id}` : (c.applies_to || "all") === "all" ? "All bookings" : `${c.applies_to} bookings`;
  const status = (c) => {
    const t = new Date().toISOString().slice(0, 10);
    if (c.active === false) return ["Inactive", "text-zinc-500"];
    if (c.starts_at && c.starts_at > t) return ["Scheduled", "text-amber-400"];
    if (c.ends_at && c.ends_at < t) return ["Expired", "text-zinc-500"];
    if (c.max_uses > 0 && (c.uses || 0) >= c.max_uses) return ["Fully redeemed", "text-zinc-500"];
    return ["Live", "text-emerald-400"];
  };

  return (
    <div data-testid="promo-codes-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-4xl uppercase">Promo Codes</h1>
          <p className="text-zinc-500 text-sm mt-1">Discounts applied at Stripe checkout for classes, workshops and events.</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={toggleEnabled} data-testid="promo-toggle" className={`px-4 py-2 text-xs uppercase tracking-widest border ${enabled ? "border-emerald-500/50 text-emerald-400" : "border-white/20 text-zinc-400"}`}>
            {enabled ? "Accepting codes · ON" : "Codes disabled · OFF"}
          </button>
          <button onClick={() => setEditing({ ...EMPTY })} className="btn-red flex items-center gap-2" data-testid="promo-new"><Plus size={14}/> New code</button>
        </div>
      </div>
      {!enabled && codes.length > 0 && (
        <div className="mb-6 border border-amber-500/40 bg-amber-500/10 text-amber-200 text-sm px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid="promo-disabled-warning">
          <span>Promo codes are switched <strong>OFF</strong> — the "Promo code" box is hidden at checkout and none of your {codes.length} code{codes.length === 1 ? "" : "s"} can be used.</span>
          <button onClick={toggleEnabled} className="btn-red !py-1.5 !px-3 !text-xs" data-testid="promo-enable-now">Turn on now</button>
        </div>
      )}

      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
            <tr>{["Code","Discount","Applies to","Window","Redemptions","Bookings","Revenue","Saved","Status",""].map(h => <th key={h} className="text-left px-4 py-3 font-normal">{h}</th>)}</tr>
          </thead>
          <tbody>
            {codes.map(c => {
              const r = report[c.code?.toUpperCase()] || {}; const [label, cls] = status(c);
              return (
                <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`promo-row-${c.code}`}>
                  <td className="px-4 py-3"><div className="font-mono font-bold flex items-center gap-2"><Tag size={12} className="text-red-500"/>{c.code}{c.show_banner && <span className="text-[9px] uppercase tracking-widest text-red-400 border border-red-500/40 px-1.5 py-0.5" data-testid={`promo-banner-flag-${c.code}`}>Banner</span>}</div>{c.description && <div className="text-zinc-500 text-xs">{c.description}</div>}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{fmt(c)}</td>
                  <td className="px-4 py-3">{scope(c)}</td>
                  <td className="px-4 py-3 text-zinc-400 whitespace-nowrap text-xs">{c.starts_at || "—"} → {c.ends_at || "—"}</td>
                  <td className="px-4 py-3">{c.uses || 0}{c.max_uses > 0 ? ` / ${c.max_uses}` : ""}</td>
                  <td className="px-4 py-3">{r.paid_bookings || 0}{r.pending_bookings ? <span className="text-zinc-500 text-xs"> (+{r.pending_bookings} pending)</span> : null}</td>
                  <td className="px-4 py-3">${Number(r.revenue || 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-emerald-400">${Number(r.discount_given || 0).toFixed(2)}</td>
                  <td className={`px-4 py-3 text-xs uppercase tracking-wider ${cls}`}>{label}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <button onClick={() => setEditing({ ...EMPTY, ...c })} className="p-1 hover:text-white text-zinc-400" data-testid={`promo-edit-${c.code}`}><Pencil size={14}/></button>
                    <button onClick={() => remove(c)} className="p-1 hover:text-red-500 text-zinc-400 ml-1" data-testid={`promo-delete-${c.code}`}><Trash2 size={14}/></button>
                  </td>
                </tr>
              );
            })}
            {codes.length === 0 && <tr><td colSpan={10} className="px-4 py-12 text-center text-zinc-500">No promo codes yet — create your first one.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && <PromoForm form={editing} setForm={setEditing} batches={batches} onSave={() => save(editing)} onClose={() => setEditing(null)} />}
    </div>
  );
}

const Field = ({ label, children }) => <div><label className="eyebrow block mb-1">{label}</label>{children}</div>;
const inp = "w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none";

function PromoForm({ form, setForm, batches, onSave, onClose }) {
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <div className="fixed inset-0 bg-black/80 z-50 grid place-items-center p-6 overflow-y-auto" onClick={onClose}>
      <div className="card-surface p-8 max-w-xl w-full my-8" onClick={e => e.stopPropagation()} data-testid="promo-form">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-display text-3xl uppercase">{form.id ? "Edit" : "New"} promo code</h2>
          <button onClick={onClose}><X size={20}/></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code"><input value={form.code} onChange={e => set("code", e.target.value.toUpperCase())} placeholder="e.g. TERM3SAVE5" className={inp + " font-mono"} data-testid="promo-code"/></Field>
          <Field label="Description (internal)"><input value={form.description || ""} onChange={e => set("description", e.target.value)} className={inp} data-testid="promo-description"/></Field>
          <Field label="Discount type">
            <select value={form.discount_type} onChange={e => set("discount_type", e.target.value)} className={inp} data-testid="promo-discount-type">
              <option value="fixed">Dollar amount off ($)</option><option value="percent">Percentage off (%)</option>
            </select>
          </Field>
          <Field label={form.discount_type === "percent" ? "Percent off" : "Dollars off"}><input type="number" min="0" step="0.5" value={form.value} onChange={e => set("value", e.target.value)} className={inp} data-testid="promo-value"/></Field>
          <Field label="Applies to">
            <select value={form.applies_to || "all"} onChange={e => { set("applies_to", e.target.value); if (e.target.value !== "class") set("batch_id", ""); }} className={inp} data-testid="promo-applies-to">
              <option value="all">All bookings</option><option value="class">Class terms</option><option value="workshop">Workshops</option><option value="event">Events</option>
            </select>
          </Field>
          <Field label="Limit to one batch (optional)">
            <select value={form.batch_id || ""} onChange={e => { set("batch_id", e.target.value); if (e.target.value) set("applies_to", "class"); }} className={inp} data-testid="promo-batch">
              <option value="">Any batch</option>
              {batches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </Field>
          <Field label="Max redemptions (0 = unlimited)"><input type="number" min="0" value={form.max_uses} onChange={e => set("max_uses", e.target.value)} className={inp} data-testid="promo-max-uses"/></Field>
          <Field label="Active">
            <select value={form.active === false ? "false" : "true"} onChange={e => set("active", e.target.value === "true")} className={inp} data-testid="promo-active"><option value="true">Yes</option><option value="false">No</option></select>
          </Field>
          <Field label="Starts (optional)"><input type="date" value={form.starts_at || ""} onChange={e => set("starts_at", e.target.value)} className={inp} data-testid="promo-starts"/></Field>
          <Field label="Ends (optional)"><input type="date" value={form.ends_at || ""} onChange={e => set("ends_at", e.target.value)} className={inp} data-testid="promo-ends"/></Field>
          <Field label="Show on homepage banner">
            <select value={form.show_banner ? "true" : "false"} onChange={e => set("show_banner", e.target.value === "true")} className={inp} data-testid="promo-show-banner"><option value="false">No</option><option value="true">Yes — show a strip at the top of the site</option></select>
          </Field>
          <Field label="Banner text (optional)"><input value={form.banner_text || ""} onChange={e => set("banner_text", e.target.value)} placeholder={`Use code ${form.code || "CODE"} for ${form.discount_type === "percent" ? `${form.value}% off` : `$${form.value} off`}`} className={inp} data-testid="promo-banner-text"/></Field>
        </div>
        <p className="text-zinc-500 text-xs mt-4">Example: “first 10 families get $5 off Juniors” → Dollar amount 5 · batch Juniors · max redemptions 10.</p>
        <div className="flex gap-3 mt-6">
          <button onClick={onSave} className="btn-red" data-testid="promo-save">Save</button>
          <button onClick={onClose} className="px-5 border border-white/20 text-sm uppercase tracking-wider">Cancel</button>
        </div>
      </div>
    </div>
  );
}
